/**
 * Policy-family AI — "easy" and "casual". Neither looks past its own next
 * move (no opponent-reply search, unlike the search family in
 * classic.js/generic.js), and both work purely off the public core API so
 * they are automatically correct in character/gimmick games (no
 * classic-model fallback needed).
 *
 * easy (choosePolicy) — mostly ignores the opponent:
 *   1. If a move wins on the spot, take it (a CPU that misses a win one
 *      cell from the goal just feels broken).
 *   2. ~10% of turns: a uniformly random pawn move (can walk backwards).
 *   3. Wall dice, reactive: if its own path is at least as long as the
 *      opponent's (i.e. it's not ahead), it usually (~90%) places its
 *      single best near-opponent wall by real 1-ply eval; if it's ahead,
 *      it only does this rarely (~5%). Calibration found this matters far
 *      more than it sounds: an opponent-blind wall (random anywhere, or
 *      even random-near-opponent) is close to worthless in this ruleset —
 *      it costs a wall and a turn for almost no effect — so a "beginner"
 *      that occasionally places walls at random is *weaker* than one that
 *      simply never bothers. Reacting only when behind, and choosing well
 *      when it does react, is what makes it feel like a real (if
 *      unambitious) opponent instead of a pushover or a coin flip.
 *   4. Otherwise: step along its own shortest path, sometimes (~35% of
 *      these turns) picking randomly among moves within +1 of its own
 *      best distance instead of the strict argmin, so it doesn't march in
 *      a dead-straight line every single game. (Only *sometimes* — paying
 *      this cost every turn compounds into a large race handicap over a
 *      whole game.)
 *
 * casual (chooseCasual) — one real ply of opponent-awareness every turn:
 * after the win-check and a small random-move chance, it scores every
 * pawn move plus every near-both-pawns wall candidate with a genuine
 * 1-ply core eval (oppDist - myDist) and plays the best one, whether or
 * not it's behind. It never looks at what the opponent does *after* that
 * (no ply-2, unlike normal), so it can still walk into a bad trade — but
 * it reliably blocks whenever blocking is the single best move, not just
 * when it's already losing.
 */

import { legalMoves, applyMove, shortestPathLength } from '../core/index.js';

function isWinningMove(state, mv, me) {
  const goalRow = state.players[me].goalRow;
  if (mv.type === 'move') return mv.to.row === goalRow;
  // Movement abilities (wallJump / diagonalStep) carry `to`; removeWall
  // carries `at` and can never win on the spot.
  if (mv.type === 'ability' && mv.to) return mv.to.row === goalRow;
  return false;
}

/** Chebyshev distance from a wall's anchor to a pawn — "is this wall near them?" */
function wallNearPawn(wall, pawn, radius) {
  const dc = Math.abs(wall.at.col - pawn.col);
  const dr = Math.abs(wall.at.row - pawn.row);
  return Math.max(dc, dr) <= radius;
}

/**
 * Wall moves whose anchor sits within `radius` tiles of at least one of
 * the given player indices' pawns (pass the same index twice for "near
 * this one pawn only"). Core-API-only (position + legality), so it stays
 * correct regardless of board size or gimmicks. Falls back to every legal
 * wall if none are near (rare — e.g. a pawn cornered by existing walls).
 */
function nearPawnWalls(state, walls, players, radius = 2) {
  const pawns = players.map((p) => state.players[p].pawn);
  const near = walls.filter((w) => pawns.some((p) => wallNearPawn(w, p, radius)));
  return near.length > 0 ? near : walls;
}

/** Greedy pawn moves: those within `slack` of the best own-distance. */
function greedyPawnMoves(state, pawn, me, slack) {
  let bestD = Infinity;
  const scored = [];
  for (const mv of pawn) {
    const next = applyMove(state, mv);
    const d = shortestPathLength(next, me);
    scored.push({ mv, d });
    if (d < bestD) bestD = d;
  }
  return scored.filter((s) => s.d <= bestD + slack + 1e-9).map((s) => s.mv);
}

const POLICY_DEFAULTS = {
  pRandomMove: 0.1,
  pWallBehind: 0.9, // wall dice when own path is not shorter than the opponent's
  pWallAhead: 0.05, // wall dice when clearly ahead
  wallRadius: 2,
  greedySlack: 1,
  // Fraction of (non-dice) turns that actually use greedySlack instead of
  // the strict argmin. Paying the "walks sideways instead of straight"
  // cost every single turn compounds into a large strength loss over a
  // whole game; doing it only sometimes still reliably breaks up the
  // dead-straight pattern without gutting easy's race pace.
  slackChance: 0.35,
};

/** Best near-opponent wall by real 1-ply core eval (oppDist - myDist). */
function bestWallNearOpponent(state, walls, me, opp, radius) {
  const near = nearPawnWalls(state, walls, [opp], radius);
  let best = near[0];
  let bestScore = -Infinity;
  for (const mv of near) {
    const next = applyMove(state, mv);
    const score = shortestPathLength(next, opp) - shortestPathLength(next, me);
    if (score > bestScore) {
      bestScore = score;
      best = mv;
    }
  }
  return best;
}

/** easy: see file header. */
export function choosePolicy(state, rng, opts = {}) {
  const { pRandomMove, pWallBehind, pWallAhead, wallRadius, greedySlack, slackChance } =
    { ...POLICY_DEFAULTS, ...opts };
  const me = state.current;
  const opp = 1 - me;
  const moves = legalMoves(state);
  if (moves.length === 1) return moves[0];

  // 1. Immediate win.
  const wins = moves.filter((mv) => isWinningMove(state, mv, me));
  if (wins.length > 0) return wins[Math.floor(rng() * wins.length)];

  const pawn = moves.filter((mv) => mv.type === 'move');
  const walls = moves.filter((mv) => mv.type === 'wall');

  // 2. Sloppiness: uniformly random pawn move.
  if (rng() < pRandomMove && pawn.length > 0) {
    return pawn[Math.floor(rng() * pawn.length)];
  }

  // 3. Wall dice, reactive: much likelier when not ahead in the race (see
  // file header for why an opponent-blind wall isn't worth bothering with).
  if (walls.length > 0) {
    const dMe = shortestPathLength(state, me);
    const dOpp = shortestPathLength(state, opp);
    const behind = dOpp >= 0 && dMe >= 0 && dMe >= dOpp;
    if (rng() < (behind ? pWallBehind : pWallAhead)) {
      return bestWallNearOpponent(state, walls, me, opp, wallRadius);
    }
  }

  // 4. Greedy: step along my own shortest path (± slack, sometimes).
  if (pawn.length > 0) {
    const slack = rng() < slackChance ? greedySlack : 0;
    const tied = greedyPawnMoves(state, pawn, me, slack);
    return tied[Math.floor(rng() * tied.length)];
  }

  // No pawn moves (e.g. swamped): anything legal.
  return moves[Math.floor(rng() * moves.length)];
}

const CASUAL_DEFAULTS = {
  pRandomMove: 0.08,
  wallChance: 0.7, // turns it even considers a wall move at all
  wallRadius: 1,
};

/** casual: see file header. */
export function chooseCasual(state, rng, opts = {}) {
  const { pRandomMove, wallChance, wallRadius } = { ...CASUAL_DEFAULTS, ...opts };
  const me = state.current;
  const opp = 1 - me;
  const moves = legalMoves(state);
  if (moves.length === 1) return moves[0];

  // 1. Immediate win.
  const wins = moves.filter((mv) => isWinningMove(state, mv, me));
  if (wins.length > 0) return wins[Math.floor(rng() * wins.length)];

  const pawn = moves.filter((mv) => mv.type === 'move');
  const walls = moves.filter((mv) => mv.type === 'wall');

  // 2. Sloppiness: uniformly random pawn move.
  if (rng() < pRandomMove && pawn.length > 0) {
    return pawn[Math.floor(rng() * pawn.length)];
  }

  // 3. One real ply: score every pawn move, and (wallChance of the time)
  // every near-both-pawns wall candidate too, by the same yardstick
  // (oppDist - myDist after the move), then play the best. No
  // opponent-reply search (that's normal's job) — it can still walk into
  // a bad trade, and doesn't always even look for a block.
  const considerWalls = rng() < wallChance;
  const candidates = considerWalls
    ? [...pawn, ...nearPawnWalls(state, walls, [me, opp], wallRadius)]
    : pawn;
  if (candidates.length === 0) {
    return moves[Math.floor(rng() * moves.length)];
  }
  let bestScore = -Infinity;
  let tied = [];
  for (const mv of candidates) {
    const next = applyMove(state, mv);
    const dMe = shortestPathLength(next, me);
    const dOpp = shortestPathLength(next, opp);
    if (dMe < 0) continue; // would strand myself (illegal wall already filtered by legalMoves)
    const score = dOpp - dMe;
    if (score > bestScore + 1e-9) {
      bestScore = score;
      tied = [mv];
    } else if (Math.abs(score - bestScore) <= 1e-9) {
      tied.push(mv);
    }
  }
  if (tied.length > 0) return tied[Math.floor(rng() * tied.length)];

  // Defensive fallback (should not happen): anything legal.
  return moves[Math.floor(rng() * moves.length)];
}
